import { FormGroup } from '@angular/forms';

import { CodeValue } from '../../shared/models/code-value';
import { TemplateVariableValues } from '../../shared/models/product';

export interface BocOpt {
	soldTo?: string | undefined;
	shipTo?: string | undefined;
	endUser?: string | undefined;
}

export type VariableFieldUpdated = Map<string, boolean | boolean[]>;

// OFFER MODELS
export type OfferStatus = 'DRAFT' | 'REQUESTED' | 'OFFERED' | 'ACCEPTED' | 'CANCELED';
export const OFFER_STATUS_LIST: OfferStatus[] = ['DRAFT', 'REQUESTED', 'OFFERED', 'ACCEPTED', 'CANCELED'];
export const DATETIME_FORMAT_PATTERN = 'YYYY-MM-DD[T]HH:mm:ss.SSSS';
export const JOIN_STRING_FOR_CONCAT = '##';
export const PRODUCT_TYPE_NF = 'NF';
export const PRODUCT_TYPE_F = 'F';
export const PRODUCT_TYPE_FM = 'FM';
export const ORGANIC_COATED_CATEGORY = 'OC';
export const FIELD_SHIPTO_PARTY = 'shipToParty';
export const FIELD_END_USER = 'endUser';
export const FIELD_STEEL_GRADE = 'steelGrade';
export const FIELD_THICKNESS = 'thicknessValue';
export const FIELD_WIDTH = 'widthValue';
export const FIELD_LENGTH = 'lengthValue';
export const FIELD_TEMPLATE_NUMBER = 'templateNumber';
export const FORM_SHEET_CODE = '7';

export const EXCLUDE_FROM_THIRD_ROW =
[
	FIELD_SHIPTO_PARTY,
	FIELD_END_USER,
	FIELD_STEEL_GRADE,
	FIELD_THICKNESS,
	FIELD_WIDTH,
	FIELD_LENGTH,
	'finishingLine'
];

export const EXCLUDE_FROM_THIRD_FOR_SIMULATION =
[
	FIELD_SHIPTO_PARTY,
	FIELD_END_USER,
	FIELD_STEEL_GRADE,
	FIELD_THICKNESS,
	FIELD_WIDTH,
	FIELD_LENGTH
];

export const FIRST_ROW_FIELDS =
[
	FIELD_STEEL_GRADE,
	FIELD_THICKNESS,
	FIELD_WIDTH,
	FIELD_LENGTH
];

export const PRODUCT_TYPE_A2C = 'a2c';
export const PRODUCT_TYPE_RTPC = 'rtpc';

type ProductType = 'a2c' | 'rtpc' | 'ufo';

export const PERIOD_TYPES = [
	{ value: 'Monthly', label: 'eoffer-Period.type.month' },
	{ value: 'Quarterly', label: 'eoffer-Period.type.quarter' },
	{ value: 'Semesterly', label: 'eoffer-Period.type.semester' },
	{ value: 'Annually', label: 'eoffer-Period.type.year' },
	{ value: 'Custom Period', label: 'eoffer-Period.type.custom' }
];

export interface Offer {
	header: OfferHeader;
	products?: OfferProduct[] | undefined;
	listOfOfferFamilyVolume?: OfferFamilyVolume[] | undefined;
}

export interface OfferLine {
	header: OfferHeader;
	product?: OfferProduct | undefined;
}

export interface OfferHeaderId {
	id?: number | undefined;
	externalId?: string | undefined;
	rfqNumber?: string | undefined;
}

export interface OfferHeader extends BocOpt, OfferHeaderId {
	rfqRef?: string | undefined;
	referenceAm?: string | undefined;
	periodStartMonth?: string | undefined;
	periodEndMonth?: string | undefined;
	idd?: string | undefined;
	tmode?: string | undefined;
	incoterm?: string | undefined;
	pricingUnit?: string | undefined;
	pricingUnitDescription?: string | undefined;
	currency?: string | undefined;
	comment?: string | undefined;
	modificationDate?: Date | undefined;
	manager?: string | undefined;
	assistant?: string | undefined;
	technician?: string | undefined;
	weightAndInvoicing?: string | undefined;
	paymentTerms?: string | undefined;
	validityDate?: Date | undefined;
	offerType?: string | undefined | null;
	incotDescription?: string | undefined;
}

interface OfferProductId {
	id?: number | undefined;
	externalId?: string | undefined;
}

export interface ProductRef {
	description: ProductShortDescriptionRef;
	ufoProductRef?: number | undefined;
}

export interface OfferProduct extends OfferProductId, ProductRef {
	description: ProductShortDescription;
	offerStatus: OfferStatus;
	feasibilityStatus: FeasibilityStatus;
	characteristics?: ProductA2c | undefined;
	rdd?: string | undefined;
	deliveryMill?: string | undefined;
	price?: number | undefined;
	a2citemNumber?: string | undefined;
	inquiryNumber?: string | undefined;
	quantity?: number | undefined;
	steelGrade?: string | undefined;
	steelGradeDescription?: string | undefined;
	thicknessValue?: number | undefined;
	widthValue?: number | undefined;
	lengthValue?: number | undefined;
	shipToParty?: string | undefined;
	endUser?: string | undefined;
	category?: string | undefined;
	form?: string | undefined;
	subCategory?: string | undefined;
	templateNumber?: number;
	productType?: string;
	configurationNumber?: number;
	isSimulationCompleted?: string;
	configErrMsg?: string;
	loader?: string;
	variableFormGroup?: FormGroup;
	/**
	 * Only for step-cart.component
	 */
	uniqueRef?: string;
	familycode?: string;
}

export interface AddToCartResponse {
	itemAdded: boolean;
	messages: string[];
}

interface ProductShortDescriptionRef {
	productRequestId?: string | undefined;
	productRef?: string | undefined;
	configurationRef?: string | undefined;
}

export interface ProductShortDescription extends ProductShortDescriptionRef {
	customerArticle?: string | undefined;
	family: ProductFamily;
	steelGrade: string;
	dimension: ProductDimension;
}

export interface ProductFamily {
	category: string;
	categoryDescription: string;
	subCategory: string;
	subCategoryDescription: string;
	form: string;
	formDescription: string;
	code: string;
	customerTemplate: Template[];
}

interface Template {
	customerTemplateNumber?: number;
	productType?: string;
}

export interface TemplateDescription {
	templateNumber: number;
	templateRelease: number;
	properties: Record<string, TemplateVariable>;
	customerTemplateReference: string;
	listOfVariables: string[];
}

export interface TemplateVariable {
	type: 'string' | 'integer' | 'number' | 'array' | 'group';
	properties?: Record<string, TemplateVariable>;
	path: string;
	mandatory: boolean;
	hidden: boolean;
	free?: boolean;
	order?: number;
}

export interface TemplateVariableString extends TemplateVariable {
	maxLength: number;
	free: boolean;
}

export interface VariableField extends TemplateVariableValues {
	keys: string;
}

export interface TemplateVariableArray extends TemplateVariable {
	properties: Record<string, TemplateVariable>;
	maxItems: number;
}

export interface ProductDimension {
	thickness: ProductDimensionValue;
	width: ProductDimensionValue;
	length?: ProductDimensionValue | undefined;
}

export interface ProductDimensionValue {
	value: number;
	unit: string;
}

export interface OfferLineAggregation {
	readonly status: OfferStatus;
	readonly countOfferLines: number;
	readonly sumQuantityInTonne: number;
	readonly countFamilies: number;
}

export interface OfferFamilyVolume {
	id?: number | undefined;
	familyCode?: string;
	pcpType?: string;
	pcpValue?: string;
	quantity?: number | undefined;
	quantityUnit?: string;
	startDate?: Date | string;
	endDate?: Date | string;
}

// PRODUCT MODELS
export type FeasibilityStatus = 'YES' | 'PENDING' | 'NO';

export interface Feasibility {
	synthesis: FeasibilityStatus;
	plants: PlantResultShort[];
}

interface PlantResultShort {
	plant: CodeValue;
	synthesis: FeasibilityStatus;
}

export interface ProductA2c {
	[key: string]: string | undefined;
	a2citemNumber: string;
	a2citemRelease: string;
	configurationNumber?: string;
	productCategoryCode: string;
	productSubCategoryCode: string;
	productFormCode: string;
	commercialSteelGradeID: string;
	thicknessValue: string;
	thicknessUnit: string;
	widthValue: string;
	widthUnit: string;
	commercialSteelGradeIDDescription: string;
	type: ProductType;
	customerTemplateNumber: string;
	customerTemplateReference: string;
	userPartReference: string;
	productType: string;
	commercialMetallicCoatingIDDescription: string;
	dimension: string;
	surfaceAspect: string;
	organicCoatingDataSheet: string;
	colourTop: string;
	removableFilm: string;
	organicCoatingDataSheetBottom: string;
	colorBottom: string;
	chemicalTreatment?: string;
	organicDatasheetNameTop?: string;
	strippableFilmType?: string;
	organicDatasheetNameBottom?: string;
	colourBottom?: string;
	edgeTrimmedInLastToolCodeDescription?: string;
	floorPlatesPattern?: string;
	floorPlatesPatternSide?: string;
	strippableFilmSide?: string;
	streakinessMaxInMicron?: string;
	betterSidePosition?: string;
	commercialThicknessToleranceIDDescription?: string;
	thicknessToleranceMinus?: string;
	thicknessTolerancePlus?: string;
	varnishTop?: string;
	varnishBottom?: string;
	primerTop?: string;
	primerBottom?: string;
	commercialTOCTypeTopID?: string;
	commercialTOCTypeTopIDDescription?: string;
	commercialTOCTypeBottomID?: string;
	commercialTOCTypeBottomIDDescription?: string;
	nominalThicknessValueBottom?: string;
	nominalThicknessValueTop?: string;
	unitOfDimensions?: string;
	lengthValue?: string;
	lengthUnit?: string;
	lastUsed?: string;
	emptyRow?: string;
	productRequestId?: string;
	productRef?: string;
	configurationRef?: string;
	quantity?: string;
	inquiryNumber?: string;
	shipToPartyCoded?: string;
	endUserIdentificationCoded?: string;
}

interface OrderDetail {
	orderNumber: string;
	orderItemNumber: string;
	productReference: string;
	productVersion: number;
	rdd: string;
	quantityInTon: number;
}

interface OrderSynthesis {
	maxProductVersion: number;
	latestRdd: string;
	orderCount: number;
	sumQuantityInTon: number;
	details: OrderDetail[];
}

export interface ProductTable {
	family: ProductFamily;
	periodInMonth: number;
	selectedFieldNames: string[];
	products: ProposedProduct[];
}

interface ProductConf {
	status: FeasibilityStatus;
	productRef: string;
	ufoProductRef: number;
	configurationRef: string;
}

export interface ProposedProduct extends ProductRef {
	description: ProductShortDescription;
	ufoProductRef?: number;
	characteristics: ProductA2c;
	orderSynthesis?: OrderSynthesis;
	configurations?: ProductConf[];
}
export interface OfferItem {
	id: number;
	opportunityID: string;
	custOffRef?: string;
	suppOffRef?: string;
	itemNumber?: string;
	standardSteelGrade?: string;
	productDimension?: ProductDimension;
	lastModifiedDate?: string;
	status: OfferStatus;
	productPrice?: string;
	productCurrency?: string;
	productQuantity?: string;
	periodStart?: string;
	periodEnd?: string;
	validityDate?: Date;
	subCategory?: string;
	form?: string;
	creationDate?: Date;
	customer?: string;
	shipTo?: string;
	endUser?: string;
	priceUnit?: string;
	priceValue?: string;
	quantityUnit?: string;
	quantityValue?: string;

}

export type FieldOfOfferItem = keyof OfferItem;

export interface OfferDraft {
	id?: number;
	customerOfferReference?: string;
	creatorPerson?: string;
	creatorPersonDescription?: string;
	status?: string;
	statusDescription?: string;
	soldTo?: string;
	soldToDescription?: string;
	shipTo?: string;
	shipToDescription?: string;
	endUser?: string;
	endUserDescription?: string;
	periodStartMonth?: string;
	periodEndMonth?: string;
	idd?: string;
	iddDescription?: string;
	tmode?: string;
	tmodeDescription?: string;
	incoterm?: string;
	incotermDescription?: string;
	weightUnit?: string;
	weightUnitDescription?: string;
	currency?: string;
	currencyDescription?: string;
	comment?: string;
	creationDate?: Date;
	modificationDate?: Date;
	lines?: OfferLineList[];
	offerFamilyVolumeList?: OfferFamilyVolume[];
	offerType?: string;
}

export type FieldOfOfferDraft = keyof OfferDraft;

interface OfferLineList {
	id: number;
	requestedQuantity: number;
	pushStatus: string;
	pushStatusDescription: string;
	creationDate: Date;
	modificationDate: Date;

}
